// Dashboard do Módulo de Parcerias
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Handshake, CreditCard, Ticket, TrendingUp, 
  CheckCircle, Clock, XCircle, AlertTriangle,
  Euro, Fuel, Filter, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const DashboardParcerias = ({ user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [financeiro, setFinanceiro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFinanceiro, setLoadingFinanceiro] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [parceriasLista, setParceriasLista] = useState([]);
  const [parceriaFiltro, setParceriaFiltro] = useState('');
  const [valesExpirar, setValesExpirar] = useState([]);

  const isAdminParceria = user?.role === 'admin_parceria';
  const isColaboradorParceria = user?.role === 'colaborador_parceria';
  const isParceria = isAdminParceria || isColaboradorParceria;

  useEffect(() => {
    loadDashboard();
    loadValesExpirar();
    if (!isParceria) {
      loadParceriasLista();
    }
  }, []);

  useEffect(() => {
    if (isAdminParceria) {
      loadFinanceiro();
    }
  }, []);

  const loadValesExpirar = async () => {
    try {
      const response = await api.get('/parcerias/alertas/vales-expirar?dias=3');
      setValesExpirar(response.data);
    } catch (error) {
      console.error('Erro ao carregar vales a expirar:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const endpoint = isParceria 
        ? '/parcerias/dashboard/parceria'
        : '/parcerias/dashboard/admin';
      const response = await api.get(endpoint);
      setDashboard(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadParceriasLista = async () => {
    try {
      const response = await api.get('/parcerias/entidades?estado=true');
      setParceriasLista(response.data);
      if (response.data.length > 0) {
        const firstId = response.data[0].id;
        setParceriaFiltro(firstId);
        loadFinanceiro(firstId);
      }
    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
    }
  };

  const loadFinanceiro = async (pidOverride) => {
    const pid = pidOverride || parceriaFiltro;
    if (!isParceria && !pid) return;
    setLoadingFinanceiro(true);
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      if (!isParceria && pid) params.append('parceria_id', pid);
      const response = await api.get(`/parcerias/dashboard/parceria/financeiro?${params.toString()}`);
      setFinanceiro(response.data);
    } catch (error) {
      console.error('Erro ao carregar financeiro:', error);
    } finally {
      setLoadingFinanceiro(false);
    }
  };

  const handleFiltrar = () => {
    loadFinanceiro();
  };

  const handleLimparFiltro = () => {
    setDataInicio('');
    setDataFim('');
    setTimeout(() => loadFinanceiro(), 0);
  };

  // Exportar dados para CSV (para admin parceria)
  const handleExportarCSV = () => {
    if (!financeiro) {
      toast.error('Não há dados para exportar');
      return;
    }

    const linhas = [];
    const nomeParceria = dashboard?.nome_parceria || 'Parceria';
    const dataExport = new Date().toLocaleDateString('pt-PT');
    
    // Cabeçalho
    linhas.push(`Relatório de Vales - ${nomeParceria}`);
    linhas.push(`Exportado em: ${dataExport}`);
    if (dataInicio || dataFim) {
      linhas.push(`Período: ${dataInicio || 'início'} até ${dataFim || 'hoje'}`);
    }
    linhas.push('');
    
    // Resumo
    linhas.push('RESUMO FINANCEIRO');
    linhas.push(`Total Validados (a receber): €${financeiro.total_desconto_validado.toFixed(2)}`);
    linhas.push(`Nº Vales Validados: ${financeiro.total_vales_validados}`);
    linhas.push(`Total Pendentes: €${financeiro.total_desconto_pendente.toFixed(2)}`);
    linhas.push(`Nº Vales Pendentes: ${financeiro.total_vales_pendentes}`);
    linhas.push('');
    
    // Vales Validados
    if (financeiro.detalhe_validados.length > 0) {
      linhas.push('VALES VALIDADOS');
      linhas.push('Nº Vale;Cliente;Data Validação;Fatura;Litros;Desconto');
      financeiro.detalhe_validados.forEach(v => {
        const dataVal = v.data_utilizacao ? new Date(v.data_utilizacao).toLocaleDateString('pt-PT') : '-';
        linhas.push(`${v.numero_vale};${v.nome_cliente || '-'};${dataVal};${v.numero_fatura_validacao || '-'};${v.litros > 0 ? v.litros.toFixed(1) : '-'};€${v.valor_desconto.toFixed(2)}`);
      });
      linhas.push('');
    }
    
    // Vales Pendentes
    if (financeiro.detalhe_pendentes.length > 0) {
      linhas.push('VALES PENDENTES');
      linhas.push('Nº Vale;Cliente;Data Emissão;Válido Até;Desconto');
      financeiro.detalhe_pendentes.forEach(v => {
        const dataEm = v.data_emissao ? new Date(v.data_emissao).toLocaleDateString('pt-PT') : '-';
        const dataVal = v.data_validade ? new Date(v.data_validade).toLocaleDateString('pt-PT') : '-';
        linhas.push(`${v.numero_vale};${v.nome_cliente || '-'};${dataEm};${dataVal};€${v.valor_desconto.toFixed(2)}`);
      });
    }
    
    // Criar e descarregar ficheiro
    const csv = linhas.join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_${nomeParceria.replace(/\s+/g, '_')}_${dataExport.replace(/\//g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Relatório exportado com sucesso');
  };

  const handleSelectParceriaAdmin = (pid) => {
    setParceriaFiltro(pid);
    loadFinanceiro(pid);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Não foi possível carregar o dashboard
        </CardContent>
      </Card>
    );
  }

  // Dashboard para Admin/Responsável Janeira
  if (!isParceria) {
    return (
      <div className="space-y-6" data-testid="dashboard-admin">
        {/* Alerta de Vales a Expirar */}
        {valesExpirar.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" />
                Vales a Expirar (próximos 3 dias)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {valesExpirar.slice(0, 10).map((v, i) => (
                  <Badge key={i} variant="outline" className="bg-white border-amber-300 text-amber-800">
                    {v.cliente}: {v.parceria} €{v.valor?.toFixed(2)} ({v.dias_restantes}d)
                  </Badge>
                ))}
                {valesExpirar.length > 10 && (
                  <Badge variant="secondary">+{valesExpirar.length - 10} mais</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cards de resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Parcerias Ativas</p>
                  <p className="text-3xl font-bold">{dashboard.total_parcerias_ativas}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Handshake className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes</p>
                  <p className="text-3xl font-bold">{dashboard.total_clientes}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <CreditCard className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Vales Válidos</p>
                  <p className="text-3xl font-bold text-green-600">{dashboard.total_vales_validos}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Vales Utilizados</p>
                  <p className="text-3xl font-bold text-gray-600">{dashboard.total_vales_utilizados}</p>
                </div>
                <div className="p-3 bg-gray-100 rounded-full">
                  <Ticket className="h-6 w-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Segunda linha de cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Vales Expirados</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.total_vales_expirados}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Vales Contingência</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboard.vales_contingencia}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Utilização</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboard.total_vales_validos + dashboard.total_vales_utilizados > 0
                      ? Math.round((dashboard.total_vales_utilizados / (dashboard.total_vales_validos + dashboard.total_vales_utilizados)) * 100)
                      : 0}%
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vales por parceria */}
        {dashboard.vales_por_parceria && Object.keys(dashboard.vales_por_parceria).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vales por Parceria</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(dashboard.vales_por_parceria).map(([nome, dados]) => (
                  <div key={nome} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="font-medium">{nome}</span>
                    <div className="flex gap-2">
                      <Badge variant="success" className="bg-green-100 text-green-700">
                        {dados.validos} válidos
                      </Badge>
                      <Badge variant="secondary">
                        {dados.utilizados} utilizados
                      </Badge>
                      <Badge variant="destructive" className="bg-red-100 text-red-700">
                        {dados.expirados} expirados
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resumo Financeiro por Parceria - Admin */}
        {parceriasLista.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-2">
                  <Euro className="h-5 w-5" />
                  Resumo Financeiro — Janeira paga
                </CardTitle>
              </div>
              <div className="flex items-end gap-3 flex-wrap pt-2">
                <div className="space-y-1">
                  <Label className="text-xs">Parceria</Label>
                  <Select value={parceriaFiltro} onValueChange={handleSelectParceriaAdmin}>
                    <SelectTrigger className="w-52" data-testid="select-parceria-financeiro">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {parceriasLista.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nome_entidade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Início</Label>
                  <Input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-40"
                    data-testid="admin-filtro-data-inicio"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Fim</Label>
                  <Input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-40"
                    data-testid="admin-filtro-data-fim"
                  />
                </div>
                <Button size="sm" onClick={handleFiltrar} data-testid="admin-btn-filtrar">
                  <Filter className="h-4 w-4 mr-1" />
                  Filtrar
                </Button>
                {(dataInicio || dataFim) && (
                  <Button size="sm" variant="outline" onClick={handleLimparFiltro}>
                    Limpar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingFinanceiro ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : financeiro ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-sm text-emerald-700 font-medium">Validados (Janeira deve)</p>
                      <p className="text-3xl font-bold text-emerald-800 mt-1">€{financeiro.total_desconto_validado.toFixed(2)}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-emerald-600">
                        <span>{financeiro.total_vales_validados} vale(s)</span>
                        {financeiro.total_litros_validados > 0 && (
                          <span>{financeiro.total_litros_validados.toFixed(1)} litros</span>
                        )}
                      </div>
                    </div>
                    <div className="p-5 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700 font-medium">Pendentes (por validar)</p>
                      <p className="text-3xl font-bold text-amber-800 mt-1">€{financeiro.total_desconto_pendente.toFixed(2)}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-amber-600">
                        <span>{financeiro.total_vales_pendentes} vale(s)</span>
                        {financeiro.total_litros_pendentes > 0 && (
                          <span>até {financeiro.total_litros_pendentes.toFixed(1)}L máx</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {financeiro.detalhe_validados.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-emerald-800">Vales Validados</h4>
                      <div className="border rounded-lg overflow-auto max-h-64">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Vale</TableHead>
                              <TableHead className="text-xs">Cliente</TableHead>
                              <TableHead className="text-xs">Data Validação</TableHead>
                              <TableHead className="text-xs">Fatura</TableHead>
                              <TableHead className="text-xs text-right">Litros</TableHead>
                              <TableHead className="text-xs text-right">Desconto</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {financeiro.detalhe_validados.map((v, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-xs font-mono">{v.numero_vale}</TableCell>
                                <TableCell className="text-xs">{v.nome_cliente || '—'}</TableCell>
                                <TableCell className="text-xs">
                                  {v.data_utilizacao ? new Date(v.data_utilizacao).toLocaleDateString('pt-PT') : '—'}
                                </TableCell>
                                <TableCell className="text-xs">{v.numero_fatura_validacao || '—'}</TableCell>
                                <TableCell className="text-xs text-right">
                                  {v.litros > 0 ? `${v.litros.toFixed(1)}L` : '—'}
                                </TableCell>
                                <TableCell className="text-xs text-right font-semibold text-emerald-700">
                                  €{v.valor_desconto.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {financeiro.detalhe_pendentes.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-amber-800">Vales Pendentes</h4>
                      <div className="border rounded-lg overflow-auto max-h-64">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Vale</TableHead>
                              <TableHead className="text-xs">Cliente</TableHead>
                              <TableHead className="text-xs">Data Emissão</TableHead>
                              <TableHead className="text-xs">Válido Até</TableHead>
                              <TableHead className="text-xs text-right">Desconto Est.</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {financeiro.detalhe_pendentes.map((v, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-xs font-mono">{v.numero_vale}</TableCell>
                                <TableCell className="text-xs">{v.nome_cliente || '—'}</TableCell>
                                <TableCell className="text-xs">
                                  {v.data_emissao ? new Date(v.data_emissao).toLocaleDateString('pt-PT') : '—'}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {v.data_validade ? new Date(v.data_validade).toLocaleDateString('pt-PT') : '—'}
                                </TableCell>
                                <TableCell className="text-xs text-right font-semibold text-amber-700">
                                  €{v.valor_desconto.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {financeiro.detalhe_validados.length === 0 && financeiro.detalhe_pendentes.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">
                      Nenhum vale encontrado para o período selecionado.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  Selecione uma parceria para ver o resumo financeiro.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Dashboard para Admin de Parceria
  return (
    <div className="space-y-6" data-testid="dashboard-parceria">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{dashboard.nome_parceria}</span>
            <Badge variant="outline" className="text-xs">Admin Parceria</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Vales Pendentes</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-green-800">{dashboard.total_vales_validos}</p>
              <p className="text-xs text-green-600 mt-1">Por utilizar no Janeira</p>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-700">
                <Ticket className="h-5 w-5" />
                <span className="text-sm font-medium">Vales Utilizados</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-blue-800">{dashboard.total_vales_utilizados}</p>
              <p className="text-xs text-blue-600 mt-1">Já validados</p>
            </div>

            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Vales Expirados</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-red-800">{dashboard.total_vales_expirados}</p>
              <p className="text-xs text-red-600 mt-1">Não utilizados</p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 text-purple-700">
                <CreditCard className="h-5 w-5" />
                <span className="text-sm font-medium">Clientes Associados</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-purple-800">{dashboard.total_clientes}</p>
              <p className="text-xs text-purple-600 mt-1">Com cartão RFID</p>
            </div>
          </div>

          {/* Estatísticas adicionais */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm font-medium">Total Emitido</span>
              </div>
              <p className="text-xl font-bold mt-1 text-emerald-800">
                {dashboard.total_vales_validos + dashboard.total_vales_utilizados + dashboard.total_vales_expirados}
              </p>
              <p className="text-xs text-emerald-600">Vales gerados (histórico)</p>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700">
                <Clock className="h-5 w-5" />
                <span className="text-sm font-medium">Taxa Utilização</span>
              </div>
              <p className="text-xl font-bold mt-1 text-amber-800">
                {dashboard.total_vales_utilizados + dashboard.total_vales_validos > 0 
                  ? Math.round((dashboard.total_vales_utilizados / (dashboard.total_vales_utilizados + dashboard.total_vales_validos + dashboard.total_vales_expirados)) * 100)
                  : 0}%
              </p>
              <p className="text-xs text-amber-600">Vales utilizados vs emitidos</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 text-gray-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="text-sm font-medium">Taxa Expiração</span>
              </div>
              <p className="text-xl font-bold mt-1 text-gray-800">
                {dashboard.total_vales_utilizados + dashboard.total_vales_validos + dashboard.total_vales_expirados > 0 
                  ? Math.round((dashboard.total_vales_expirados / (dashboard.total_vales_utilizados + dashboard.total_vales_validos + dashboard.total_vales_expirados)) * 100)
                  : 0}%
              </p>
              <p className="text-xs text-gray-600">Vales expirados vs emitidos</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo Financeiro - só para admin da parceria */}
      {isAdminParceria && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Resumo Financeiro — A pagar pelo Janeira
            </CardTitle>
            <Button size="sm" variant="outline" onClick={handleExportarCSV} disabled={!financeiro} data-testid="btn-exportar-csv">
              <Download className="h-4 w-4 mr-1" />
              Exportar CSV
            </Button>
          </div>
          {/* Filtro de Datas */}
          <div className="flex items-end gap-3 flex-wrap pt-2">
            <div className="space-y-1">
              <Label className="text-xs">Data Início</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-40"
                data-testid="filtro-data-inicio"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Fim</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-40"
                data-testid="filtro-data-fim"
              />
            </div>
            <Button size="sm" onClick={handleFiltrar} data-testid="btn-filtrar">
              <Filter className="h-4 w-4 mr-1" />
              Filtrar
            </Button>
            {(dataInicio || dataFim) && (
              <Button size="sm" variant="outline" onClick={handleLimparFiltro}>
                Limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingFinanceiro ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : financeiro ? (
            <div className="space-y-6">
              {/* Cards financeiros */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-700 font-medium">Validados (Janeira deve)</p>
                  <p className="text-3xl font-bold text-emerald-800 mt-1">€{financeiro.total_desconto_validado.toFixed(2)}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-emerald-600">
                    <span>{financeiro.total_vales_validados} vale(s)</span>
                    {financeiro.total_litros_validados > 0 && (
                      <span>{financeiro.total_litros_validados.toFixed(1)} litros</span>
                    )}
                  </div>
                </div>
                <div className="p-5 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 font-medium">Pendentes (por validar)</p>
                  <p className="text-3xl font-bold text-amber-800 mt-1">€{financeiro.total_desconto_pendente.toFixed(2)}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-amber-600">
                    <span>{financeiro.total_vales_pendentes} vale(s)</span>
                    {financeiro.total_litros_pendentes > 0 && (
                      <span>até {financeiro.total_litros_pendentes.toFixed(1)}L máx</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabela de vales validados */}
              {financeiro.detalhe_validados.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-emerald-800">Vales Validados</h4>
                  <div className="border rounded-lg overflow-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Vale</TableHead>
                          <TableHead className="text-xs">Cliente</TableHead>
                          <TableHead className="text-xs">Data Validação</TableHead>
                          <TableHead className="text-xs">Fatura</TableHead>
                          <TableHead className="text-xs text-right">Litros</TableHead>
                          <TableHead className="text-xs text-right">Desconto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financeiro.detalhe_validados.map((v, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs font-mono">{v.numero_vale}</TableCell>
                            <TableCell className="text-xs">{v.nome_cliente || '—'}</TableCell>
                            <TableCell className="text-xs">
                              {v.data_utilizacao ? new Date(v.data_utilizacao).toLocaleDateString('pt-PT') : '—'}
                            </TableCell>
                            <TableCell className="text-xs">{v.numero_fatura_validacao || '—'}</TableCell>
                            <TableCell className="text-xs text-right">
                              {v.litros > 0 ? `${v.litros.toFixed(1)}L` : '—'}
                            </TableCell>
                            <TableCell className="text-xs text-right font-semibold text-emerald-700">
                              €{v.valor_desconto.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Tabela de vales pendentes */}
              {financeiro.detalhe_pendentes.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-amber-800">Vales Pendentes</h4>
                  <div className="border rounded-lg overflow-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Vale</TableHead>
                          <TableHead className="text-xs">Cliente</TableHead>
                          <TableHead className="text-xs">Data Emissão</TableHead>
                          <TableHead className="text-xs">Válido Até</TableHead>
                          <TableHead className="text-xs text-right">Desconto Est.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financeiro.detalhe_pendentes.map((v, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs font-mono">{v.numero_vale}</TableCell>
                            <TableCell className="text-xs">{v.nome_cliente || '—'}</TableCell>
                            <TableCell className="text-xs">
                              {v.data_emissao ? new Date(v.data_emissao).toLocaleDateString('pt-PT') : '—'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {v.data_validade ? new Date(v.data_validade).toLocaleDateString('pt-PT') : '—'}
                            </TableCell>
                            <TableCell className="text-xs text-right font-semibold text-amber-700">
                              €{v.valor_desconto.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {financeiro.detalhe_validados.length === 0 && financeiro.detalhe_pendentes.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Nenhum vale encontrado para o período selecionado.
                </p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default DashboardParcerias;
